import { addRemoveFavouritesApi } from '@api/useQueryCalls';
import { Ionicons } from '@expo/vector-icons';
import { useToast } from '@hooks';
import { FEATURE_FLAGS } from '@root/constants';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS, AMPLITUDE_EVENT_SOURCES } from '@utils/analytics/constants';
import { scale } from '@utils/normalize';
import { showSuccessToast } from '@utils/toastManager';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Platform, Pressable } from 'react-native';
import useFeatureFlag from './useFeatureFlag';

function getFavouriteTag(isFavourite: boolean) {
  return isFavourite ? 'add' : 'remove';
}
function getStarIconName(isFavourite: boolean) {
  return isFavourite ? 'star' : 'star-outline';
}

function getFavouriteColor(isFavourite: boolean) {
  return isFavourite ? colors.warning.sunflower : colors.neutral.cotton;
}

function getMarginRight() {
  return Platform.OS === 'web' ? scale(10) : scale(2);
}

function getIdForToastMessage(isFav: any): string {
  return isFav ? 'fav' : 'unfav';
}

function getToastMessageForFav(isFav: any, t): string {
  return isFav ? t('favourites.add') : t('favourites.remove');
}

export function logFavouriteAction(isFavourite: boolean, source: string, prefix: string): void {
  const e = AMPLITUDE_CONSTANTS.favourites;
  AmplitudeHelper.logEvent(e.name, {
    [e.attributes.source]: source,
    [e.attributes.transaction_category_id]: prefix,
    [e.attributes.action_type]: getFavouriteTag(isFavourite), // opposite since the state is toggled
  });
}

const useFavourites = (navigation, categoryId, isFavouriteFromlist, source, prefix, canAddFavorites) => {
  const [isFavourite, setIsFavourite] = useState(isFavouriteFromlist);
  const [showFavourites, setShowFavourites] = useState(canAddFavorites);
  const toast = useToast();
  const didMount = useRef(false);

  const [isFeatureAvailable] = useFeatureFlag({
    featureName: [FEATURE_FLAGS.FAV_HOMEPAGE],
  });

  const showFavouritesFeature = isFeatureAvailable[FEATURE_FLAGS.FAV_HOMEPAGE]?.show;
  const { t } = useTranslation('common');

  const { mutate: isFavouriteApi } = addRemoveFavouritesApi({
    payload: {
      categoryId,
      favorite: isFavourite,
    },
  });

  const bounceAnim = useRef(new Animated.Value(0)).current;
  const bounceLoopRef = useRef<Animated.CompositeAnimation | null>(null);

  const startBouncing = useCallback(() => {
    bounceLoopRef.current = Animated.loop(
      Animated.sequence([
        Animated.timing(bounceAnim, {
          toValue: -10,
          duration: 180,
          useNativeDriver: true,
        }),
        Animated.spring(bounceAnim, {
          toValue: 0,
          friction: 3,
          tension: 100,
          useNativeDriver: true,
        }),
        Animated.delay(700),
      ]),
    );
    bounceLoopRef.current.start();
  }, [bounceAnim]);

  const stopBouncing = useCallback(() => {
    bounceLoopRef.current?.stop();
    bounceAnim.setValue(0);
  }, [bounceAnim]);

  const onPressToast = (isFav: boolean) => {
    showSuccessToast(toast, getToastMessageForFav(isFav, t), getIdForToastMessage(isFav), 'top', 1600);
  };

  useEffect(() => {
    if (didMount.current) {
      if (showFavourites) {
        onPressToast(isFavourite);
      }
    } else {
      didMount.current = true;
    }
  }, [isFavourite, showFavourites]);

  const handlePress = useCallback(() => {
    setIsFavourite(prev => {
      const newFav = !prev;
      isFavouriteApi();
      logFavouriteAction(newFav, AMPLITUDE_EVENT_SOURCES.product_detail, prefix);
      setShowFavourites(true);
      return newFav;
    });
  }, [categoryId, source, prefix]);

  useEffect(() => {
    if (!isFavourite) {
      startBouncing();
    } else {
      stopBouncing();
    }
  }, [isFavourite, startBouncing, stopBouncing]);

  const renderHeaderRight = useCallback(
    () => (
      <Pressable onPress={handlePress} style={{ marginRight: getMarginRight() }}>
        <Animated.View style={{ transform: [{ translateY: bounceAnim }] }}>
          <Ionicons name={getStarIconName(isFavourite)} size={scale(22)} color={getFavouriteColor(isFavourite)} />
        </Animated.View>
      </Pressable>
    ),
    [handlePress, isFavourite, bounceAnim],
  );

  useLayoutEffect(() => {
    const shouldShowFavourite = showFavourites || isFavourite;
    if (showFavouritesFeature && shouldShowFavourite) {
      navigation.setOptions({ headerRight: renderHeaderRight });
    }
  }, [navigation, showFavouritesFeature, showFavourites, isFavourite, renderHeaderRight]);

  return isFavourite;
};

export default useFavourites;
